import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { sortGrades } from "../../utils/SchoolAdmin/translations";

/**
 * SLE belongs in the Groups subject dropdown when the teacher can rate SLE
 * for this school/grade: own homeroom, or a shared group with an SLE share.
 */
export const hasGroupSleAccess = ({
    allSharedGroupOptions,
    gradingProfilesCurrentSem,
    selectedEpr,
    selectedGrade,
    selectedSchoolLocationKey
}) => {
    const hasHomeroomAtGrade = Boolean(
        (selectedEpr && (!selectedGrade || selectedEpr.gradeName === selectedGrade)) ||
            (selectedGrade && (gradingProfilesCurrentSem || []).some((p) => p.gradeName === selectedGrade))
    );
    if (hasHomeroomAtGrade) return true;

    return (allSharedGroupOptions || []).some((group) => {
        if (!group.sharedMeta?.groupSleShareDto) return false;
        if (selectedGrade && group.sharedGradeName && group.sharedGradeName !== selectedGrade) return false;
        if (selectedSchoolLocationKey && Number(group.sharedLocationKey) !== Number(selectedSchoolLocationKey)) {
            return false;
        }
        return true;
    });
};

/**
 * Manages the school → grade → subject → group selection sequence for GROUPS mode.
 *
 * Encapsulates:
 * - Grade/school derivations from shared group options and own EPR profiles
 * - Group options rebuild (homeroom + shared groups, SLE vs subject mode)
 * - Preselected group restoration from router state
 * - Reset cascade handlers
 * - handleGroupGradeChange and handleSubjectSelect
 *
 * handleSubjectSelect handles navigation and SLE toggling but does not call
 * setSelectedTemplate — GroupRatings wraps it and looks up the subject itself.
 * That avoids a circular dependency with useSubjectSelection (which needs
 * combinedGrades from this hook).
 *
 * @param {Object}   params
 * @param {Array}    params.allSharedGroupOptions
 * @param {Array}    params.gradingProfilesCurrentSem
 * @param {Object}   params.locationState
 * @param {string}   params.pathname
 * @param {Object}   params.selectedEpr
 * @param {string}   params.selectedGrade
 * @param {Function} params.setSelectedGrade
 * @param {number}   params.selectedSchoolLocationKey
 * @param {Object}   params.selectedTemplate
 * @param {Function} params.setSelectedTemplate
 * @param {boolean}  params.sleSelected
 * @param {Function} params.setSleSelected
 * @param {Array}    params.sharedGradingProfileDtos
 * @param {Function} params.setSubjectOptions
 * @param {Function} params.dispatch
 * @param {Object}   params.ratingState
 *   { resetRatings, resetComments, setExistingRatingsData, setExistingStudentComments }
 */
const useGroupSelection = ({
    allSharedGroupOptions,
    gradingProfilesCurrentSem,
    locationState,
    pathname,
    selectedEpr,
    selectedGrade,
    setSelectedGrade,
    selectedSchoolLocationKey,
    selectedTemplate,
    setSelectedTemplate,
    sleSelected,
    setSleSelected,
    sharedGradingProfileDtos,
    setSubjectOptions,
    dispatch,
    ratingState
}) => {
    const navigate = useNavigate();

    const [groupOptions, setGroupOptions] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);

    const { resetRatings, resetComments, setExistingRatingsData, setExistingStudentComments } = ratingState;

    // ── Grade / school derivations ─────────────────────────────────────────────
    const sharedLocationKeys = useMemo(
        () => [...new Set((allSharedGroupOptions || []).map((g) => g.sharedLocationKey).filter(Boolean))],
        [allSharedGroupOptions]
    );

    const homeroomGrades = useMemo(
        () => [...new Set((gradingProfilesCurrentSem || []).map((g) => g.gradeName).filter(Boolean))],
        [gradingProfilesCurrentSem]
    );

    const sharedGrades = useMemo(
        () => [...new Set((allSharedGroupOptions || []).map((g) => g.sharedGradeName).filter(Boolean))],
        [allSharedGroupOptions]
    );

    const combinedGrades = useMemo(
        () => [...new Set([...homeroomGrades, ...sharedGrades])],
        [homeroomGrades, sharedGrades]
    );

    const sharedGradesForSelectedSchool = useMemo(() => {
        if (!selectedSchoolLocationKey) return [];
        return [
            ...new Set(
                (allSharedGroupOptions || [])
                    .filter((g) => Number(g.sharedLocationKey) === Number(selectedSchoolLocationKey))
                    .map((g) => g.sharedGradeName)
                    .filter(Boolean)
            )
        ];
    }, [allSharedGroupOptions, selectedSchoolLocationKey]);

    const gradesForGroupSelect = useMemo(() => {
        const hasMultipleSchools = (sharedLocationKeys?.length ?? 0) > 1;
        const hasSelectedSchool = Boolean(selectedSchoolLocationKey);
        let shared = sharedGrades ?? [];
        if (hasMultipleSchools && !hasSelectedSchool) shared = [];
        else if (hasSelectedSchool) shared = sharedGradesForSelectedSchool ?? [];
        return sortGrades([...new Set([...(homeroomGrades ?? []), ...shared])]);
    }, [homeroomGrades, sharedGrades, sharedGradesForSelectedSchool, sharedLocationKeys, selectedSchoolLocationKey]);

    // ── Preselected group IDs from router state ────────────────────────────────
    const preselectedGroupIds = useMemo(() => {
        const raw = locationState?.preselectedGroupIds;
        if (!raw) return [];
        return Array.isArray(raw) ? raw.map(String) : [String(raw)];
    }, [locationState]);

    // ── Render condition flags ─────────────────────────────────────────────────
    const shouldShowGroupEprSelect = combinedGrades.length > 1 && selectedSchoolLocationKey;
    const showSchoolSelect = sharedLocationKeys.length > 1;

    // ── Reset helpers ──────────────────────────────────────────────────────────
    const resetSubjectCaches = useCallback(() => {
        resetRatings();
        resetComments();
        setExistingRatingsData([]);
        setExistingStudentComments([]);
    }, [resetRatings, resetComments, setExistingRatingsData, setExistingStudentComments]);

    const resetGroups = useCallback(() => {
        setSelectedGroups([]);
        setGroupOptions([]);
    }, []);

    const resetForSubjectChange = useCallback(() => {
        resetSubjectCaches();
        resetGroups();
        setSelectedTemplate(null);
    }, [resetSubjectCaches, resetGroups, setSelectedTemplate]);

    const resetForGroupGradeChange = useCallback(() => {
        navigate(pathname, { replace: true });
        resetSubjectCaches();
        resetGroups();
        setSubjectOptions([]);
        setSelectedTemplate(null);
        setSleSelected(false);
    }, [navigate, pathname, resetSubjectCaches, resetGroups, setSubjectOptions, setSelectedTemplate, setSleSelected]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleGroupGradeChange = useCallback(
        (e) => {
            const gradeName = e.target.value;
            setSelectedGrade(gradeName);
            const profile = gradingProfilesCurrentSem?.find((p) => p.gradeName === gradeName) || null;
            dispatch({ type: "SET_SELECTED_EPR", payload: profile });
            resetForGroupGradeChange();
        },
        [gradingProfilesCurrentSem, dispatch, resetForGroupGradeChange, setSelectedGrade]
    );

    // handleSubjectSelect handles navigation + SLE toggle + resets.
    // It does not call setSelectedTemplate for non-SLE subjects — GroupRatings
    // looks up the subject after this to avoid a circular dependency with
    // useSubjectSelection (which needs combinedGrades).
    const handleSubjectSelect = useCallback(
        (value) => {
            if (!value) return;
            resetForSubjectChange();

            if (value === "SLE") {
                setSleSelected(true);
                setSelectedTemplate(null);
                navigate(pathname, {
                    replace: true,
                    state: { ...locationState, subjectId: null, sleSelected: true, preselectedGroupIds: null }
                });
                return;
            }

            setSleSelected(false);
            const templateId = Number(value);
            if (templateId) {
                navigate(pathname, {
                    replace: true,
                    state: { ...locationState, subjectId: templateId, sleSelected: false, preselectedGroupIds: null }
                });
            }
            // Caller must call setSelectedTemplate(subjectOptions.find(...)) after this
        },
        [resetForSubjectChange, setSleSelected, setSelectedTemplate, navigate, pathname, locationState]
    );

    // ── Apply preselected groups from router state ─────────────────────────────
    useEffect(() => {
        if (!groupOptions?.length) return;
        if (!preselectedGroupIds.length) return;
        if (locationState?.selectedSchoolLocationKey && !selectedSchoolLocationKey) return;

        const wanted = new Set(preselectedGroupIds);
        const flat = groupOptions.flatMap((g) => g?.options || []);
        const matches = flat.filter((opt) => {
            const match = /(\d+)$/.exec(String(opt?.value || ""));
            const tail = match ? match[1] : undefined;
            const sharedId = opt?.sharedMeta?.sharedGroupId ? String(opt.sharedMeta.sharedGroupId) : null;
            return (tail && wanted.has(tail)) || (sharedId && wanted.has(sharedId));
        });

        const isSameSelection = (prev) =>
            prev.length === matches.length && prev.every((p) => matches.some((m) => m.value === p.value));

        setSelectedGroups((prev) => (isSameSelection(prev) ? prev : matches));
    }, [groupOptions, preselectedGroupIds, locationState, selectedSchoolLocationKey]);

    // ── Rebuild group options when shared group metadata changes ──────────────
    useEffect(() => {
        if (!allSharedGroupOptions) return;

        if (sleSelected) {
            const effectiveEpr =
                selectedEpr ||
                (selectedGrade ? gradingProfilesCurrentSem?.find((p) => p.gradeName === selectedGrade) : null);

            const homeroomSleOptions =
                effectiveEpr?.homeroomGroups
                    ?.filter((g) => !g.name?.startsWith("INTERNAL_"))
                    .map((g) => ({
                        value: `homeroom-${g.key}`,
                        label: g.name,
                        groupType: g.type || "HOMEROOM",
                        homeroomMeta: g
                    })) || [];

            const sleSharedGroups = (allSharedGroupOptions ?? [])
                .filter((group) => {
                    const sleShare = group.sharedMeta?.groupSleShareDto;
                    if (!sleShare) return false;
                    if (selectedGrade && group.sharedGradeName && group.sharedGradeName !== selectedGrade) return false;
                    if (
                        selectedSchoolLocationKey &&
                        Number(group.sharedLocationKey) !== Number(selectedSchoolLocationKey)
                    )
                        return false;
                    return true;
                })
                .map((group) => {
                    const sleShare = group.sharedMeta.groupSleShareDto;
                    const permission = sleShare.permission || "VIEW";
                    return {
                        value: group.value,
                        label: `${group.label} — ${permission === "EDIT" ? "Edit" : "View Only"}`,
                        groupType: group.groupType,
                        sharedGradeName: group.sharedGradeName,
                        sharedGradingProfile: group.sharedGradingProfile,
                        sharedMeta: group.sharedMeta,
                        permission
                    };
                });

            const nextSections = [];
            if (homeroomSleOptions.length)
                nextSections.push({ label: "Homeroom Groups (SLE)", options: homeroomSleOptions });
            if (sleSharedGroups.length)
                nextSections.push({ label: "Groups Shared With Me (SLE)", options: sleSharedGroups });
            setGroupOptions(nextSections);
            return;
        }

        if (!selectedTemplate) return;

        const targetTemplateId = Number(selectedTemplate.templateId);
        const effectiveEpr =
            selectedEpr ||
            (selectedGrade ? gradingProfilesCurrentSem?.find((p) => p.gradeName === selectedGrade) : null);

        const homeroomGroupOptions =
            effectiveEpr?.homeroomGroups
                ?.filter((g) => !g.name?.startsWith("INTERNAL_"))
                .map((g) => ({
                    value: `homeroom-${g.key}`,
                    label: g.name,
                    groupType: g.type || "HOMEROOM",
                    homeroomMeta: g
                })) || [];

        const criteriaToTemplate = new Map(
            (sharedGradingProfileDtos ?? []).flatMap((dto) =>
                (dto.gradingCriteriaList ?? []).map((c) => [String(c.key), Number(c.parentTemplate)])
            )
        );

        const sharedGroupOptions = (allSharedGroupOptions ?? [])
            .filter((group) => {
                if (selectedGrade && group.sharedGradeName && group.sharedGradeName !== selectedGrade) return false;
                if (selectedSchoolLocationKey && Number(group.sharedLocationKey) !== Number(selectedSchoolLocationKey))
                    return false;
                return true;
            })
            .map((group) => {
                const shares = group.sharedMeta?.groupShareDto?.gradingCriteriaShares ?? [];
                const matchingShare = shares.find(
                    (share) => criteriaToTemplate.get(String(share.gradingCriteriaId)) === targetTemplateId
                );
                if (matchingShare === undefined) return null;
                return {
                    ...group,
                    permission: matchingShare.permission,
                    matchingCriteriaId: matchingShare.gradingCriteriaId,
                    sharedGradingProfileId: group.sharedGradingProfile
                };
            })
            .filter(Boolean);

        const nextSections = [];
        if (homeroomGroupOptions.length) nextSections.push({ label: "Homeroom Groups", options: homeroomGroupOptions });
        if (sharedGroupOptions.length) {
            nextSections.push({
                label: "Groups Shared With Me",
                options: sharedGroupOptions.map((g) => ({
                    value: g.value,
                    label: `${g.label} — ${g.permission === "EDIT" ? "Edit" : "View Only"}`,
                    groupType: g.groupType,
                    sharedGradeName: g.sharedGradeName,
                    sharedGradingProfile: g.sharedGradingProfile,
                    sharedMeta: g.sharedMeta,
                    permission: g.permission,
                    matchingCriteriaId: g.matchingCriteriaId,
                    sharedGradingProfileId: g.sharedGradingProfileId
                }))
            });
        }
        setGroupOptions(nextSections);
    }, [
        allSharedGroupOptions,
        gradingProfilesCurrentSem,
        selectedEpr,
        selectedGrade,
        selectedSchoolLocationKey,
        selectedTemplate,
        sharedGradingProfileDtos,
        sleSelected
    ]);

    const hasSleSubjectOption = useMemo(
        () =>
            hasGroupSleAccess({
                allSharedGroupOptions,
                gradingProfilesCurrentSem,
                selectedEpr,
                selectedGrade,
                selectedSchoolLocationKey
            }),
        [allSharedGroupOptions, gradingProfilesCurrentSem, selectedEpr, selectedGrade, selectedSchoolLocationKey]
    );

    // Accordion "View/Input Ratings" for SLE navigates with sleSelected:true before
    // shared groups have loaded. Clearing immediately would drop SLE; restore once
    // access is known, and only clear when we can evaluate that access is missing.
    useEffect(() => {
        if (hasSleSubjectOption) {
            if (locationState?.sleSelected) setSleSelected(true);
            return;
        }

        if (!sleSelected) return;

        const hasHomeroomAtGrade = Boolean(
            (selectedEpr && (!selectedGrade || selectedEpr.gradeName === selectedGrade)) ||
                (selectedGrade && (gradingProfilesCurrentSem || []).some((p) => p.gradeName === selectedGrade))
        );
        const sharedGroupsHaveArrived = (allSharedGroupOptions?.length ?? 0) > 0;

        // Deep-link SLE for a shared-only teacher: wait until shares arrive.
        if (locationState?.sleSelected && !hasHomeroomAtGrade && !sharedGroupsHaveArrived) {
            return;
        }

        setSleSelected(false);
    }, [
        allSharedGroupOptions,
        gradingProfilesCurrentSem,
        hasSleSubjectOption,
        locationState?.sleSelected,
        selectedEpr,
        selectedGrade,
        setSleSelected,
        sleSelected
    ]);

    return {
        combinedGrades,
        gradesForGroupSelect,
        groupOptions,
        handleGroupGradeChange,
        handleSubjectSelect,
        hasSleSubjectOption,
        preselectedGroupIds,
        resetForGroupGradeChange,
        resetForSubjectChange,
        selectedGroups,
        setSelectedGroups,
        shouldShowGroupEprSelect,
        showSchoolSelect,
        sharedLocationKeys
    };
};

export default useGroupSelection;
